import { Alert, Button, Link, Typography } from "@mui/material";
import { convert } from "portal/utils/units/units";
import { FeatureFlag } from "portal/utils/hooks/useFeatureFlag";
import { getActivePlantProfileId } from "portal/utils/categoryCollection/plantProfile";
import {
  getPlantProfilesPath,
  getRobotPath,
  isPlantProfileEditPath,
  RobotSubpath,
} from "portal/utils/routing";
import { Loading } from "portal/components/Loading";
import {
  Navigate,
  Link as RouterLink,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { PlantProfile } from "portal/components/categoryCollectionProfile/PlantProfile";
import { portalEndpoints } from "portal/state/portalApi";
import { ScopedCustomerMissingFeatureFlagAlert } from "portal/components/customers/ScopedCustomerMissingFeatureFlagAlert";
import { skipToken } from "@reduxjs/toolkit/query";
import { Trans, useTranslation } from "react-i18next";
import { useQueryPopups } from "portal/utils/hooks/useApiPopups";
import ExternalIcon from "@mui/icons-material/OpenInNewOutlined";
import OperatorIcon from "@mui/icons-material/TabletMac";
import PlantProfilesIcon from "@mui/icons-material/YardOutlined";
import React, {
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from "react";

export const RobotActivePlantProfile: FC = () => {
  const { t } = useTranslation();
  const { serial } = useParams<{ serial: string }>();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const { currentData: summary, isLoading } = useQueryPopups(
    portalEndpoints.getRobot.useQuery(
      serial
        ? {
            serial,
            includeCustomer: true,
            includeHealthLogs: true,
            includeDailyMetrics: false,
          }
        : skipToken,
      {
        pollingInterval: convert(1).from("min").to("ms"),
        skipPollingIfUnfocused: true,
        refetchOnFocus: true,
      }
    ),
    { errorVariant: "warning" }
  );

  const plantProfileBasePath = getRobotPath(serial, RobotSubpath.PLANT_PROFILE);
  const robotMatches = summary?.robot?.serial === serial;
  const activeId = robotMatches ? getActivePlantProfileId(summary) : undefined;
  const customerUuid = robotMatches ? summary?.customer?.uuid : undefined;
  const isEditing = isPlantProfileEditPath(pathname);

  const [pinnedEditUuid, setPinnedEditUuid] = useState<string | undefined>();
  useEffect(() => {
    if (!isEditing) {
      setPinnedEditUuid(undefined);
      return;
    }
    setPinnedEditUuid((current) => current ?? activeId);
  }, [isEditing, activeId]);

  const uuid = (isEditing ? pinnedEditUuid : undefined) ?? activeId;
  const activeChanged = Boolean(
    isEditing && pinnedEditUuid && pinnedEditUuid !== activeId
  );

  const { isError: isProfileError, isSuccess: isProfileSuccess } =
    portalEndpoints.getCustomerPlantProfile.useQuery(
      uuid && customerUuid ? { uuid, customerUuid } : skipToken
    );

  const onReloadActive = useCallback(() => {
    if (!serial) {
      return;
    }
    navigate(plantProfileBasePath, { replace: true });
  }, [navigate, plantProfileBasePath, serial]);

  if (!serial) {
    return;
  }

  if (isLoading || !robotMatches) {
    return <Loading />;
  }
  if (!customerUuid) {
    return;
  }

  if (!uuid) {
    if (isEditing) {
      return <Navigate to={plantProfileBasePath} replace />;
    }
    return (
      <div className="flex flex-col gap-4">
        <ScopedCustomerMissingFeatureFlagAlert
          flag={FeatureFlag.CATEGORY_COLLECTION}
          explicitCustomerUuid={customerUuid}
        />
        <div className="pt-8 flex items-center justify-center">
          <Typography className="flex gap-2 flex-wrap">
            <Trans
              i18nKey="views.robots.plantProfiles.noActive"
              components={{
                profilesLink: <PlantProfilesLink customerUuid={customerUuid} />,
              }}
            />
          </Typography>
        </div>
      </div>
    );
  }

  if (isProfileError) {
    return (
      <div className="flex flex-col gap-4">
        <ScopedCustomerMissingFeatureFlagAlert
          flag={FeatureFlag.CATEGORY_COLLECTION}
          explicitCustomerUuid={customerUuid}
        />
        {activeChanged && (
          <Alert
            severity="info"
            action={
              <Button color="inherit" size="small" onClick={onReloadActive}>
                {t("utils.actions.reload")}
              </Button>
            }
          >
            {t("views.robots.plantProfiles.activeChanged")}
          </Alert>
        )}
        <div className="pt-8 flex items-center justify-center">
          <Typography className="flex gap-2 flex-wrap">
            <Trans
              i18nKey="views.robots.plantProfiles.notFound"
              components={{
                profilesLink: <PlantProfilesLink customerUuid={customerUuid} />,
              }}
            />
          </Typography>
        </div>
      </div>
    );
  }

  if (!isProfileSuccess) {
    return <Loading />;
  }

  return (
    <div className="flex flex-col gap-4">
      <ScopedCustomerMissingFeatureFlagAlert
        flag={FeatureFlag.CATEGORY_COLLECTION}
        explicitCustomerUuid={customerUuid}
      />
      {activeChanged ? (
        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" onClick={onReloadActive}>
              {t("utils.actions.reload")}
            </Button>
          }
        >
          {t("views.robots.plantProfiles.activeChanged")}
        </Alert>
      ) : (
        <Alert severity="warning">
          <Trans
            i18nKey="views.robots.plantProfiles.activeBanner"
            values={{
              robotName: summary?.robot?.customerSerial ?? serial,
            }}
            components={{
              profilesLink: <PlantProfilesLink customerUuid={customerUuid} />,
              operatorLink: <OperatorAppLink serial={serial} />,
            }}
          />
        </Alert>
      )}
      <PlantProfile
        key={uuid}
        uuid={uuid}
        customerUuid={customerUuid}
        robotSummary={summary}
        useSetMutation={
          portalEndpoints.applyCustomerPlantProfileChanges.useMutation
        }
      />
    </div>
  );
};

const OperatorAppLink: FC<PropsWithChildren<{ serial: string }>> = ({
  serial,
  children,
}) => {
  const operatorUrl = window._jsenv.REACT_APP_OPERATOR_URL;
  if (!operatorUrl) {
    return children;
  }
  const href = new URL(
    `robots/${encodeURIComponent(serial)}`,
    operatorUrl.endsWith("/") ? operatorUrl : `${operatorUrl}/`
  ).href;
  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      underline="always"
      color="primary"
      className="inline-flex items-center gap-1"
    >
      <OperatorIcon fontSize="inherit" />
      {children}
      <ExternalIcon fontSize="inherit" />
    </Link>
  );
};

const PlantProfilesLink: FC<{ customerUuid?: string }> = ({ customerUuid }) => {
  const { t } = useTranslation();
  return (
    <Link
      component={RouterLink}
      to={getPlantProfilesPath(customerUuid)}
      underline="always"
      color="primary"
      className="inline-flex items-center gap-1"
    >
      <PlantProfilesIcon fontSize="inherit" />
      {t("views.plantProfiles.title")}
    </Link>
  );
};
